// Generic entity picker (formerly CustomerPickerRenderer). Two source
// modes: REST API (built-in customers endpoint by default; any list
// URL supported) or Transaction object (rows returned by the Vision
// live-preview executor for a chosen transaction). On selection writes
// the picked record to bound page vars via `outputBindings`, matching
// the transaction-embed row-selection pattern.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { EntityPickerBlock } from '@/pages/designers/page/types'
import {
  apiGet, erpErrorMessage, extractItems,
} from '../erpService'
import { livePreviewList, type ListFilter } from '../api'
import {
  outputVarName, resolveArgsAsRecord, resolveRef, writeOutputs,
} from '../bindingResolver'
import { resolveEndpointUrl } from './endpoints'
import { injectEcomStyles } from './styles'

// Loose row shape — every list endpoint returns objects with at least
// some identifying fields. We render whatever's most descriptive.
type Row = Record<string, unknown> & {
  id?: string | number
  _txn_id?: string | number
  name?: string
  phone?: string
  email?: string
  priorOrderCount?: number
  lastVisitDate?: string
}

function pickId(r: Row): string | undefined {
  if (r.id !== undefined && r.id !== null) return String(r.id)
  if (r._txn_id !== undefined && r._txn_id !== null) return String(r._txn_id)
  return undefined
}

function pickName(r: Row): string {
  if (typeof r.name === 'string' && r.name) return r.name
  // fall back to first string field that looks like a name
  for (const k of ['title', 'label', 'displayName', 'fullName']) {
    const v = r[k]
    if (typeof v === 'string' && v) return v
  }
  return pickId(r) || '(no name)'
}

function pickSub(r: Row): string {
  if (typeof r.phone === 'string' && r.phone) return r.phone
  if (typeof r.email === 'string' && r.email) return r.email
  return ''
}

function initials(name?: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || name.charAt(0).toUpperCase()
}

export default function EntityPickerRenderer({ block }: { block: EntityPickerBlock }) {
  injectEcomStyles()
  const runtime = useRuntime()

  const ctx = useMemo(() => ({ vars: runtime.vars, inputs: runtime.page.inputs || [] }), [runtime.vars, runtime.page.inputs])

  // Resolve the destination var for the "whole record" output so we
  // can rehydrate `selected` from the page state on mount (and when
  // the var is cleared externally).
  const valueOutput = block.outputBindings?.find((o) => o.target === 'value')
  const valueVarName = outputVarName(valueOutput)

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Row[]>([])
  const [selected, setSelected] = useState<Row | null>(() => {
    if (!valueVarName) return null
    const cur = runtime.vars[valueVarName]
    return cur && typeof cur === 'object' ? (cur as Row) : null
  })
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reqIdRef = useRef(0)

  const showCreateNewToggle = block.showCreateNewToggle !== false
  const layout = block.layout || 'spacious'
  const placeholder = block.searchPlaceholder || 'Search by phone or name'

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  function runSearch(q: string) {
    const myReq = ++reqIdRef.current
    setLoading(true)
    setErrorMsg('')

    const finish = (items: Row[]) => {
      if (reqIdRef.current !== myReq) return
      setResults(items)
      setLoading(false)
    }
    const fail = (err: unknown, fallback: string) => {
      if (reqIdRef.current !== myReq) return
      setErrorMsg(erpErrorMessage(err, fallback))
      setResults([])
      setLoading(false)
    }

    if (block.source.kind === 'api') {
      const url = resolveEndpointUrl(block.source)
      if (!url) {
        fail(null, 'No endpoint configured. Set a URL in the block source.')
        return
      }
      const params = resolveArgsAsRecord(block.source.args, ctx)
      // The search query always overrides any bound `q` param so the
      // text field stays the primary signal.
      params.q = q
      apiGet(url, params)
        .then((data) => finish(extractItems(data) as Row[]))
        .catch((err) => fail(err, 'Failed to load list.'))
    } else {
      // Transaction source — use the live-preview list executor. The
      // configured filters are resolved against page vars.
      const project = runtime.project
      const objName = block.source.obj_name
      if (!project || !objName) {
        fail(null, 'Pick a transaction object in the block source.')
        return
      }
      const normalize = (col: string) => (col || '').trim().replace(/\s+/g, '_').toLowerCase()
      const resolvedFilters: ListFilter[] = (block.source.filters || [])
        .filter((f) => !!f.column && !!f.op)
        .map((f) => {
          if (f.op === 'between') {
            return { column: normalize(f.column), op: f.op,
              from: resolveRef(f.from, ctx), to: resolveRef(f.to, ctx) }
          }
          return { column: normalize(f.column), op: f.op,
            value: resolveRef(f.value, ctx) }
        })
      livePreviewList(project, objName, 50, 0, undefined, undefined, resolvedFilters)
        .then((res) => finish((res?.rows || []) as Row[]))
        .catch((err: unknown) => fail(err, 'Failed to load transaction rows.'))
    }
  }

  function onQueryChange(q: string) {
    setQuery(q)
    if (selected && q !== pickName(selected)) {
      setSelected(null)
      writeOutputs(block.outputBindings, { value: null }, runtime.setVar)
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(q.trim()), 220)
  }

  function select(row: Row) {
    setSelected(row)
    writeOutputs(
      block.outputBindings,
      { value: row, ...row },
      runtime.setVar,
    )
    const toggleName = outputVarName(block.toggleBinding)
    if (toggleName) runtime.setVar(toggleName, false)
    setResults([])
    setQuery(pickName(row))
  }

  function clearSelection() {
    setSelected(null)
    writeOutputs(block.outputBindings, { value: null }, runtime.setVar)
    setQuery('')
    setResults([])
  }

  function toggleNew() {
    const toggleName = outputVarName(block.toggleBinding)
    if (!toggleName) return
    runtime.setVar(toggleName, true)
    setSelected(null)
    setResults([])
  }

  return (
    <section className={`cp${layout === 'compact' ? ' cp--compact' : ''}`}>
      <div className="cp__bar">
        <span className="cp__icon" aria-hidden="true">🔎</span>
        <input
          className="cp__input"
          type="search"
          placeholder={placeholder}
          value={query}
          onChange={(e) => onQueryChange(e.currentTarget.value)}
          autoComplete="off"
        />
        {showCreateNewToggle && (
          <button className="cp__newbtn" type="button" onClick={toggleNew}>
            + New
          </button>
        )}
      </div>

      {selected ? (
        <div className="cp__selected">
          <div className="cp__avatar">{initials(pickName(selected))}</div>
          <div className="cp__selected-meta">
            <div className="cp__name">{pickName(selected)}</div>
            <div className="cp__sub">{pickSub(selected)}</div>
            {(selected.priorOrderCount || 0) > 0 ? (
              <div className="cp__chip cp__chip--returning">
                Returning · {selected.priorOrderCount} prior order{selected.priorOrderCount === 1 ? '' : 's'}
                {selected.lastVisitDate ? ` · last visit ${selected.lastVisitDate}` : ''}
              </div>
            ) : pickSub(selected) ? (
              <div className="cp__chip cp__chip--new">Selected</div>
            ) : null}
          </div>
          <button className="cp__change" type="button" onClick={clearSelection}>Change</button>
        </div>
      ) : loading ? (
        <div className="cp__hint">Searching…</div>
      ) : errorMsg ? (
        <div className="cp__error">{errorMsg}</div>
      ) : results.length === 0 && query.length > 0 ? (
        <div className="cp__hint">
          No matches.
          {showCreateNewToggle && (
            <button className="cp__inline" type="button" onClick={toggleNew}>
              Create new →
            </button>
          )}
        </div>
      ) : results.length > 0 ? (
        <ul className="cp__list">
          {results.map((r, i) => {
            const id = pickId(r) || String(i)
            const name = pickName(r)
            const sub = pickSub(r)
            return (
              <li
                key={id}
                className="cp__row"
                onClick={() => select(r)}
                aria-label={`Select ${name}`}
              >
                <div className="cp__avatar cp__avatar--sm">{initials(name)}</div>
                <div className="cp__row-meta">
                  <div className="cp__name">{name}</div>
                  {sub && <div className="cp__sub">{sub}</div>}
                </div>
                {(r.priorOrderCount || 0) > 0 ? (
                  <span className="cp__chip cp__chip--returning">
                    {r.priorOrderCount} prior · {r.lastVisitDate || '—'}
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </section>
  )
}
